import React from 'react';
import { Product, ProductType } from '@inter-ikea-storageone/range';
import * as R from 'ramda';

export type TypeOption = {
  id: string;
  name: string;
  types: string[];
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
};

export type TypeFilterProps = {
  options: TypeOption[];
  products: Product[];
  filterState: Record<string, ProductType[]>;
  onFilter: (productIdList: string[], filterState: any) => void;
  isFilterVisible: boolean;
  children: any;
  id?: string;
};

export type TypeFilterChildProps = Omit<TypeFilterProps, 'children'>;

export type OnApplyFilterType = (types: string[], resetOldFilter?: boolean) => void;

export type TypeFilterRenderProps = TypeFilterProps & {
  onApplyFilter: OnApplyFilterType;
};

function TypeFilter({
  options,
  products,
  filterState,
  onFilter,
  isFilterVisible,
  children
}: TypeFilterProps) {
  const onApplyFilter = (types: string[], resetOldFilter?: boolean) => {
    if (!isFilterVisible) return;

    const filterMap: Record<string, ProductType[]> = options.reduce((acc, curr) => {
      if (R.equals([...curr.types].sort(), [...types].sort())) {
        return {
          ...acc,
          [curr.id]: filterState[curr.id] && filterState[curr.id].length > 0 ? [] : types
        };
      }
      return acc;
    }, {});

    let newFilterState = { ...filterState, ...filterMap };
    if (resetOldFilter) {
      newFilterState = filterMap;
    }

    const filterApplied = Object.values(newFilterState).some(f => f.length > 0);

    let filteredProducts = products;
    if (filterApplied) {
      const filteredTypes = Object.values(newFilterState).flatMap(t => t);
      filteredProducts = filteredProducts.filter(product => filteredTypes.includes(product.type));
    }

    const filteredProductsId = filteredProducts.map(p => p.coreProductId);
    onFilter(filteredProductsId, newFilterState);
  };

  return (
    <>
      {children({
        onApplyFilter,
        options,
        products,
        filterState,
        onFilter,
        isFilterVisible,
        children
      })}
    </>
  );
}

export default TypeFilter;
